import { useMemo } from "react";
import { addWrappedEventListener, addDocumentEventListener } from "owa-event-listener";
import { lazyDismissPopup } from "owa-selection-popup";
import { lazyHandleSelection } from "./lazyFunctions";
import { resolveSelectionRegion } from "./regions";

const DEBOUNCE_DELAY = 300;

type ResolvedSelectionRegion = NonNullable<ReturnType<typeof resolveSelectionRegion>>;

const isNode = (target: EventTarget | null): target is Node => target !== null && "nodeType" in target && typeof target.nodeType === "number";

function resolveSelection(targetWindow: Window, startTarget?: Node, endTarget?: Node): ResolvedSelectionRegion | undefined {
    const selection = targetWindow.getSelection();
    const selectedText = selection?.toString().trim();
    if (!selection || !selectedText || selection.rangeCount === 0) {
        return undefined;
    }
    
    const range = selection.getRangeAt(0);
    const candidate = resolveSelectionRegion(range.startContainer);
    if (!candidate) {
        return undefined;
    }
    
    const containsEnd = candidate.element.contains(range.endContainer);
    const gestureStayedWithinSubject = !candidate.region.isBody &&
        !!startTarget &&
        !!endTarget &&
        candidate.element.contains(startTarget) &&
        candidate.element.contains(endTarget);
    
    // Chromium can place the range endpoint after a `user-select: none` control even when
    // the gesture starts and ends within the subject. Trust the pointer targets for that case;
    // gestures that enter the subject from another region still require endpoint containment.
    return containsEnd || gestureStayedWithinSubject ? candidate : undefined;
}

function createReadingPaneSelectionRef(targetWindow: Window): (element: HTMLElement | null) => void {
    let listenerDisposers: Array<(() => void) | undefined> = [];
    let gestureStartTarget: Node | undefined;
    let isMouseDown = false;
    let pendingSelectionTimeout: number | undefined;
    
    const cancelPendingSelection = () => {
        if (pendingSelectionTimeout !== undefined) {
            targetWindow.clearTimeout(pendingSelectionTimeout);
            pendingSelectionTimeout = undefined;
        }
    };
    
    const resetGesture = () => {
        isMouseDown = false;
        gestureStartTarget = undefined;
    };
    
    const processSelection = (startTarget?: Node, endTarget?: Node) => {
        cancelPendingSelection();
        const resolved = resolveSelection(targetWindow, startTarget, endTarget);
        if (!resolved) {
            lazyDismissPopup.importAndExecute();
            return;
        }
        
        pendingSelectionTimeout = targetWindow.setTimeout(() => {
            pendingSelectionTimeout = undefined;
            void lazyHandleSelection.importAndExecute(targetWindow, resolved.element, resolved.region.item, resolved.region.isBody);
        }, DEBOUNCE_DELAY);
    };
    
    const onMouseDown = (ev?: MouseEvent) => {
        cancelPendingSelection();
        isMouseDown = true;
        gestureStartTarget = ev && isNode(ev.target) ? ev.target : undefined;
        lazyDismissPopup.importAndExecute();
    };
    
    const onMouseUp = (ev?: MouseEvent) => {
        if (!isMouseDown) {
            return;
        }
        
        const startTarget = gestureStartTarget;
        resetGesture();
        if (ev?.button === 0) {
            processSelection(startTarget, isNode(ev.target) ? ev.target : undefined);
        }
    };
    
    const onContextMenu = () => {
        cancelPendingSelection();
        resetGesture();
        lazyDismissPopup.importAndExecute();
    };
    
    const onSelectionChange = () => {
        if (!isMouseDown) {
            processSelection();
        }
    };
    
    const detachListeners = () => {
        for (const dispose of listenerDisposers) {
            dispose?.();
        }
        listenerDisposers = [];
        cancelPendingSelection();
        resetGesture();
    };
    
    return element => {
        detachListeners();
        if (!element) {
            return;
        }
        
        listenerDisposers = [
            addWrappedEventListener("ReadingPaneSelection_MouseDown", element, "mousedown", onMouseDown),
            addWrappedEventListener("ReadingPaneSelection_ContextMenu", element, "contextmenu", onContextMenu),
            addDocumentEventListener("ReadingPaneSelection_MouseUp", "mouseup", onMouseUp, targetWindow.document),
            addDocumentEventListener("ReadingPaneSelection_SelectionChange", "selectionchange", onSelectionChange, targetWindow.document),
        ];
    };
}

/**
 * Reading-pane selection framework: delegated listeners at the reading-pane root classify
 * selections within registered subject and message-body regions. Returns a callback ref to attach
 * to the reading-pane root element.
 */
export function useReadingPaneSelection(targetWindow: Window): (element: HTMLElement | null) => void {
    return useMemo(() => createReadingPaneSelectionRef(targetWindow), [targetWindow]);
}
